import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ShoppingCart, Menu, X, Search, Phone } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useCart } from '@/contexts/CartContext'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { gtmEvents } from '@/lib/gtm'

export const Header = () => {
  const { itemsCount } = useCart()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const navigate = useNavigate()

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchTerm.trim()) {
      gtmEvents.custom('search', { search_term: searchTerm })
      navigate(`/shop?q=${encodeURIComponent(searchTerm)}`)
      setIsMenuOpen(false)
    }
  }

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Loja', path: '/shop' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contato', path: '/contato' },
  ]

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/80 backdrop-blur-md supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto px-4 h-16 flex items-center justify-between">
        {/* Logo - Rebranded to DP6.shop */}
        <Link
          to="/"
          className="text-xl font-bold tracking-tight flex items-center gap-2"
        >
          <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center text-primary-foreground font-mono">
            DP6
          </div>
          DP6.shop
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-6">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className="text-sm font-medium transition-colors hover:text-primary"
            >
              {link.name}
            </Link>
          ))}
        </nav>

        {/* Actions */}
        <div className="hidden md:flex items-center gap-4">
          <form onSubmit={handleSearch} className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Buscar..."
              className="w-48 lg:w-64 pl-9 h-9"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </form>

          <Button
            variant="ghost"
            size="icon"
            onClick={() =>
              gtmEvents.custom('click_whatsapp', { location: 'header' })
            }
            asChild
          >
            <a
              href="https://wa.me/5511999999999"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Fale no WhatsApp"
            >
              <Phone className="h-5 w-5" />
            </a>
          </Button>

          <Link to="/carrinho" className="relative group">
            <Button variant="outline" size="icon" className="relative">
              <ShoppingCart className="h-5 w-5 transition-transform group-hover:scale-110" />
              {itemsCount > 0 && (
                <Badge className="absolute -top-2 -right-2 h-5 w-5 flex items-center justify-center p-0 animate-in zoom-in">
                  {itemsCount}
                </Badge>
              )}
            </Button>
          </Link>
        </div>

        {/* Mobile Menu Toggle */}
        <div className="flex items-center gap-4 md:hidden">
          <Link to="/carrinho" className="relative">
            <ShoppingCart className="h-6 w-6" />
            {itemsCount > 0 && (
              <Badge className="absolute -top-2 -right-2 h-4 w-4 flex items-center justify-center p-0 text-[10px]">
                {itemsCount}
              </Badge>
            )}
          </Link>
          <button onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden border-t bg-background p-4 animate-in slide-in-from-top-5">
          <form onSubmit={handleSearch} className="mb-4 relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Buscar..."
              className="w-full pl-9"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </form>
          <nav className="flex flex-col gap-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMenuOpen(false)}
                className="text-lg font-medium py-2 border-b border-border/50"
              >
                {link.name}
              </Link>
            ))}
            <a
              href="https://wa.me/5511999999999"
              target="_blank"
              rel="noopener noreferrer"
              className="text-lg font-medium py-2 text-green-600 flex items-center gap-2"
              onClick={() =>
                gtmEvents.custom('click_whatsapp', { location: 'mobile_menu' })
              }
            >
              <Phone className="h-5 w-5" /> Fale no WhatsApp
            </a>
          </nav>
        </div>
      )}
    </header>
  )
}
